import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Navigate, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import { SkillsLandingPage } from '@/src/components/skills/skills-landing-page';
import { AppShell } from '@/src/components/layout/app-shell';
import { CommunityLandingPage } from '@/src/components/community/community-landing-page';
import { CoursesPage } from '@/src/pages/courses';
import { MyOrdersPage } from '@/src/pages/orders';
import { AccountCenterPage } from '@/src/pages/account-center';
import { CourseDetailPage } from '@/src/pages/courses/course-detail';
import { LessonPage } from '@/src/pages/courses/lesson';
import { GameMapPage } from '@/src/pages/courses/game-map';
import { GameLevelPage } from '@/src/pages/courses/game-level';
import { CreatorCenterPage } from '@/src/pages/creator-center';
import { PublishFlow } from '@/src/components/community/publish-flow';
import { PostDetailPage } from '@/src/components/community/post-detail-page';
import { UserProfilePage } from '@/src/components/community/user-profile-page';
import { TopBar } from '@/src/components/layout/top-bar';
import { DemoScenariosPanel } from '@/src/components/layout/demo-scenarios-panel';
import { LoginModal } from '@/src/components/auth/login-modal';
import { PublishModal } from '@/src/components/community/publish-modal';
import type { PublishModalInitialDraft } from '@/src/components/community/publish-modal';
import { CourseCreateModal } from '@/src/components/course/course-create-modal';
import type { CourseCreateEditData } from '@/src/components/course/course-create-modal';
import { SkillPublishModal } from '@/src/components/skills/skill-publish-modal';
import type { SkillPublishEditData } from '@/src/components/skills/skill-publish-modal';
import { PromoBanner } from '@/src/components/billing/promo-banner';
import { VoucherGrantModal } from '@/src/components/billing/voucher-grant-modal';
import { useDemoContext } from '@/lib/demo-context';
import { useAppContext } from '@/lib/app-context';
import { useAuthContext } from '@/lib/auth-context';
import { useToast } from '@/lib/hooks/use-toast';
import { handleOAuthCallback } from '@/lib/api/auth';
import { claimBenefit } from '@/lib/api/wallet';
import type { UserInfoResponse as UserInfo } from '@/lib/api';
import type { ClaimedBenefitItem } from '@/lib/api/wallet';
import type { CommunityPost } from '@/lib/community/types';

/* ── Lazy-loaded tower pages (heavy: mock data + animations) ── */
const TowerMapPage = lazy(() => import('@/src/tower-engine/pages/tower-map').then((m) => ({ default: m.TowerMapPage })));
const TowerFloorPage = lazy(() => import('@/src/tower-engine/pages/tower-floor').then((m) => ({ default: m.TowerFloorPage })));
const TowerReportPage = lazy(() => import('@/src/tower-engine/pages/tower-report').then((m) => ({ default: m.TowerReportPage })));
const TowerLobbyPage = lazy(() => import('@/src/tower-engine/pages/tower-lobby').then((m) => ({ default: m.TowerLobbyPage })));
const TowerEnterprisePage = lazy(() => import('@/src/tower-engine/pages/tower-enterprise').then((m) => ({ default: m.TowerEnterprisePage })));
const TowerHeroSelectPage = lazy(() => import('@/src/tower-engine/pages/tower-hero-select').then((m) => ({ default: m.TowerHeroSelectPage })));
const WorkplacePage = lazy(() => import('@/src/workplace/workplace-page'));

function UserProfilePageWrapper({ onClaimBenefits }: { onClaimBenefits?: () => void }) {
  const { userId } = useParams<{ userId: string }>();
  return <UserProfilePage onClaimBenefits={onClaimBenefits} userId={userId || ''} />;
}

function PostDetailPageWrapper() {
  const { postId } = useParams<{ postId: string }>();
  return <PostDetailPage postId={postId || ''} />;
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { demoSteps, chatQuery, selectedScenarioId, scenarioList, selectScenario } = useDemoContext();
  const { mode } = useAppContext();
  const { isLoggedIn, showLoginModal, openLogin, closeLogin, onLoginSuccess } = useAuthContext();
  const toast = useToast();
  const [promoDismissed, setPromoDismissed] = useState(() => localStorage.getItem('safeclaw:promo-3000-dismissed') === '1');
  const [voucherBenefits, setVoucherBenefits] = useState<ClaimedBenefitItem[]>([]);
  const voucherOpen = voucherBenefits.length > 0;

  /* 战斗页及 Workplace 隐藏 TopBar（沉浸式全屏） */
  const isTowerBattlePage = /\/academy\/tower\/.+\/floor\/\d+/.test(location.pathname);
  const isWorkplacePage = location.pathname === '/workplace';
  const hideTopBar = isTowerBattlePage || isWorkplacePage;

  const claimAndShowBenefits = useCallback(() => {
    claimBenefit()
      .then((res) => {
        if (res.list && res.list.length > 0) {
          setVoucherBenefits(res.list);
        }
      })
      .catch(() => {
        // 权益领取失败不影响当前流程，静默忽略
      });
  }, []);

  const handleLoginSuccess = useCallback(() => {
    onLoginSuccess();
    sessionStorage.removeItem('safeclaw:voucher-3000-shown');
    toast.success('登录成功，欢迎回来！');
    // 登录成功后尝试领取权益
    claimBenefit()
      .then((res) => {
        if (res.list && res.list.length > 0) {
          setVoucherBenefits(res.list);
        }
      })
      .catch(() => {
        // 权益领取失败不影响登录流程，静默忽略
      });
  }, [onLoginSuccess, toast]);

  /* ── OAuth 回调处理：URL 参数携带 token/refreshToken/userInfo 时自动登录 ── */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const refreshToken = params.get('refreshToken');
    const userInfoRaw = params.get('userInfo');
    if (!token || !refreshToken || !userInfoRaw) return;

    try {
      const userInfo = JSON.parse(decodeURIComponent(userInfoRaw)) as UserInfo;
      handleOAuthCallback(token, refreshToken, userInfo);
      sessionStorage.removeItem('safeclaw:voucher-3000-shown');
      onLoginSuccess();
      toast.success('登录成功，欢迎回来！');
      // OAuth 登录成功后尝试领取权益
      claimBenefit()
        .then((resp) => {
          if (resp.list && resp.list.length > 0) {
            setVoucherBenefits(resp.list);
          }
        })
        .catch(() => {
          // 静默忽略
        });
    } catch {
      toast.error('第三方登录失败，请重试');
    } finally {
      // 清除 URL 中的 OAuth 参数，避免刷新重复处理
      params.delete('token');
      params.delete('refreshToken');
      params.delete('userInfo');
      const clean = params.toString();
      const newUrl = window.location.pathname + (clean ? `?${clean}` : '');
      window.history.replaceState({}, '', newUrl);
    }
    // 仅挂载时执行一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Publish modal (global, all community routes) ── */
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishInitialDraft, setPublishInitialDraft] = useState<PublishModalInitialDraft | null>(null);
  const [publishEditPost, setPublishEditPost] = useState<CommunityPost | null>(null);
  const [showCourseCreateModal, setShowCourseCreateModal] = useState(false);
  const [courseCreateEditCourse, setCourseCreateEditCourse] = useState<CourseCreateEditData | null>(null);
  const [skillPublishOpen, setSkillPublishOpen] = useState(false);
  const [skillPublishEditSkill, setSkillPublishEditSkill] = useState<SkillPublishEditData | null>(null);

  useEffect(() => {
    document.documentElement.classList.toggle('safeclaw-promo-active', !isLoggedIn && !promoDismissed && !hideTopBar);
    return () => document.documentElement.classList.remove('safeclaw-promo-active');
  }, [hideTopBar, isLoggedIn, promoDismissed]);

  const handleDismissPromo = useCallback(() => {
    localStorage.setItem('safeclaw:promo-3000-dismissed', '1');
    setPromoDismissed(true);
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = event instanceof CustomEvent ? (event.detail as (PublishModalInitialDraft & { editPost?: CommunityPost }) | undefined) : undefined;
      setPublishEditPost(detail?.editPost ?? null);
      setPublishInitialDraft(detail?.editPost ? null : detail ?? null);
      if (!isLoggedIn) {
        openLogin();
        return;
      }
      setShowPublishModal(true);
    };
    window.addEventListener('safeclaw:open-publish', handler);
    return () => window.removeEventListener('safeclaw:open-publish', handler);
  }, [isLoggedIn, openLogin]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = event instanceof CustomEvent ? (event.detail as { editCourse?: CourseCreateEditData } | undefined) : undefined;
      setCourseCreateEditCourse(detail?.editCourse ?? null);
      if (!isLoggedIn) {
        openLogin();
        return;
      }
      setShowCourseCreateModal(true);
    };
    window.addEventListener('safeclaw:open-course-create', handler);
    return () => window.removeEventListener('safeclaw:open-course-create', handler);
  }, [isLoggedIn, openLogin]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = event instanceof CustomEvent ? (event.detail as { editSkill?: SkillPublishEditData } | undefined) : undefined;
      setSkillPublishEditSkill(detail?.editSkill ?? null);
      if (!isLoggedIn) {
        openLogin();
        return;
      }
      setSkillPublishOpen(true);
    };
    window.addEventListener('safeclaw:open-skill-publish', handler);
    return () => window.removeEventListener('safeclaw:open-skill-publish', handler);
  }, [isLoggedIn, openLogin]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {!hideTopBar && <TopBar />}
      {!hideTopBar && !isLoggedIn && !promoDismissed && <PromoBanner onClose={handleDismissPromo} onLogin={openLogin} />}
      {(mode === 'chat-landing' || mode === 'chat-session') && !isWorkplacePage && (
        <DemoScenariosPanel onSelectScenario={selectScenario} scenarioList={scenarioList} selectedScenarioId={selectedScenarioId} />
      )}
      {showLoginModal && <LoginModal onClose={closeLogin} onLoginSuccess={handleLoginSuccess} />}
      <Routes>
        {/* 临时、可撤销：默认首页指向学院；恢复时换回原 LandingPage 路由。保留 query 以兼容 OAuth 回调。 */}
        <Route element={<Navigate replace to={{ pathname: '/academy', search: location.search }} />} path="/" />
        <Route
          element={
            <AppShell
              demoSteps={demoSteps}
              initialQuery={chatQuery}
              onGoToLanding={() => navigate('/')}
              onGoToSkills={() => navigate('/skills')}
              selectedScenarioId={selectedScenarioId}
            />
          }
          path="/chat"
        />
        <Route
          element={<SkillsLandingPage isLoggedIn={isLoggedIn} onGoToChat={() => navigate('/chat')} onGoToLanding={() => navigate('/')} onGoToLogin={openLogin} />}
          path="/skills"
        />
        <Route element={<PublishFlow />} path="/community/publish" />
        <Route element={<PostDetailPageWrapper />} path="/community/post/:postId" />
        <Route element={<UserProfilePageWrapper onClaimBenefits={claimAndShowBenefits} />} path="/community/user/:userId" />
        <Route element={<CommunityLandingPage />} path="/community/*" />
        <Route element={<CoursesPage />} path="/academy" />
        <Route
          element={
            <Suspense>
              <TowerMapPage />
            </Suspense>
          }
          path="/academy/tower/:towerId/map"
        />
        <Route
          element={
            <Suspense>
              <TowerHeroSelectPage />
            </Suspense>
          }
          path="/academy/tower/:towerId/hero-select/:floorNumber"
        />
        <Route
          element={
            <Suspense>
              <TowerFloorPage />
            </Suspense>
          }
          path="/academy/tower/:towerId/floor/:floorNumber"
        />
        <Route
          element={
            <Suspense>
              <TowerReportPage />
            </Suspense>
          }
          path="/academy/tower/report/:runId"
        />
        <Route
          element={
            <Suspense>
              <TowerEnterprisePage />
            </Suspense>
          }
          path="/academy/tower/enterprise"
        />
        <Route
          element={
            <Suspense>
              <TowerLobbyPage />
            </Suspense>
          }
          path="/academy/tower"
        />
        <Route
          element={
            <Suspense>
              <WorkplacePage />
            </Suspense>
          }
          path="/workplace"
        />

        <Route element={<GameMapPage />} path="/academy/game/:courseId" />
        <Route element={<GameLevelPage />} path="/academy/game/:courseId/:chapterId" />
        <Route element={<CourseDetailPage />} path="/academy/:courseId" />
        <Route element={<LessonPage />} path="/academy/:courseId/:chapterId" />
        <Route element={<AccountCenterPage />} path="/account-center" />
        <Route element={<MyOrdersPage />} path="/orders" />
        <Route element={<CreatorCenterPage />} path="/creator-center" />
      </Routes>

      <VoucherGrantModal
        benefits={voucherBenefits}
        onClose={() => setVoucherBenefits([])}
        onGoToAccount={() => {
          setVoucherBenefits([]);
          navigate('/account-center');
        }}
        open={voucherOpen}
      />

      {/* Global publish modal for all community routes */}
      <PublishModal
        editPost={publishEditPost ?? undefined}
        initialDraft={publishInitialDraft}
        onClose={() => {
          setShowPublishModal(false);
          setPublishInitialDraft(null);
          setPublishEditPost(null);
        }}
        onSuccess={() => window.dispatchEvent(new CustomEvent('safeclaw:creator-content-changed'))}
        open={showPublishModal}
      />
      <CourseCreateModal
        editCourse={courseCreateEditCourse}
        onClose={() => {
          setShowCourseCreateModal(false);
          setCourseCreateEditCourse(null);
        }}
        onSuccess={() => window.dispatchEvent(new CustomEvent('safeclaw:creator-content-changed'))}
        open={showCourseCreateModal}
      />
      <SkillPublishModal
        editSkill={skillPublishEditSkill}
        onClose={() => {
          setSkillPublishOpen(false);
          setSkillPublishEditSkill(null);
        }}
        onSuccess={() => window.dispatchEvent(new CustomEvent('safeclaw:creator-content-changed'))}
        open={skillPublishOpen}
      />
    </div>
  );
}
